import { useState } from 'react';
import { Plus, Flag, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { goalTypesForGrade } from '@/lib/goalOptions';
import { useExamCatalog } from '@/hooks/useExamCatalog';
import { daysUntil, MAX_EXAM_TARGETS, ExamTargetError } from '@/services/examTargetService';
import { useExamTargets } from '@/hooks/useExamTargets';

interface Step2Props {
  gradeBand: string;
  initialGoalType?: string;
  onNext: (goalType: string) => void;
  onBack: () => void;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export function Step2GoalType({ gradeBand, initialGoalType = '', onNext, onBack }: Step2Props) {
  const [goalType, setGoalType] = useState(initialGoalType);
  const options = goalTypesForGrade(gradeBand);

  // exam targets (real API via hook)
  const { targets, add, remove } = useExamTargets();
  const { exams } = useExamCatalog();
  const [showForm, setShowForm] = useState(false);
  const [examId, setExamId] = useState('');
  const [otherExamName, setOtherExamName] = useState('');
  const [examDate, setExamDate] = useState('');
  const [targetScore, setTargetScore] = useState('');
  const [isResit, setIsResit] = useState<boolean | null>(null);

  async function handleAddExam() {
    if (examId === '__other__') {
      if (!otherExamName.trim()) { toast.error('Enter an exam name.'); return; }
      try {
        await add({ examName: otherExamName.trim(), examDate, targetScore: targetScore || undefined, isResit });
        setShowForm(false); setExamId(''); setOtherExamName(''); setExamDate(''); setTargetScore(''); setIsResit(null);
      } catch (e) {
        toast.error(e instanceof ExamTargetError ? e.message : 'Could not add exam target.');
      }
    } else {
      const exam = exams.find((e) => e.id === examId);
      if (!exam) { toast.error('Choose an exam.'); return; }
      try {
        await add({ examName: exam.name, catalogExamId: exam.id, examDate, targetScore: targetScore || undefined, isResit });
        setShowForm(false); setExamId(''); setExamDate(''); setTargetScore(''); setIsResit(null);
      } catch (e) {
        toast.error(e instanceof ExamTargetError ? e.message : 'Could not add exam target.');
      }
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-1">What are you working towards?</h2>
        <p className="text-sm text-muted-foreground">Pick your main goal — add an exam if you're preparing for one.</p>
      </div>

      {/* Goal type chips */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Goal type <span className="text-red-500">*</span></label>
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setGoalType((p) => (p === opt ? '' : opt))}
              className={cn(
                'px-3.5 py-2 rounded-full text-sm font-medium border transition-colors cursor-pointer',
                goalType === opt
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:border-primary/50 hover:bg-muted/50'
              )}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Exam targets (optional, up to 3) */}
      <div className="border-t border-border pt-4 space-y-2">
        <div className="flex items-center">
          <label className="text-sm font-medium text-foreground">Exam targets</label>
          <span className="text-xs text-muted-foreground ml-2">optional · up to {MAX_EXAM_TARGETS}</span>
          {targets.length < MAX_EXAM_TARGETS && !showForm && (
            <button onClick={() => setShowForm(true)} className="ml-auto text-xs text-primary flex items-center gap-0.5">
              <Plus className="w-3.5 h-3.5" /> Add exam
            </button>
          )}
        </div>

        {targets.map((t) => {
          const d = daysUntil(t.examDate);
          return (
            <div key={t.id} className="flex items-center gap-2 bg-muted/40 rounded-lg px-3 py-2">
              <Flag className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm">{t.examName}</div>
                <div className="text-xs text-muted-foreground">
                  {new Date(t.examDate).toLocaleDateString()} · {d >= 0 ? `${d} days` : 'past'}
                  {t.targetScore ? ` · ${t.targetScore}` : ''}{t.isResit ? ' · Retaking' : ''}
                </div>
              </div>
              <button onClick={() => remove(t.id)} className="text-muted-foreground hover:text-red-500" aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}

        {showForm && (
          <div className="border border-border rounded-xl p-3 space-y-3 bg-muted/30">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Exam <span className="text-red-500">*</span></label>
                <select value={examId} onChange={(e) => setExamId(e.target.value)} className="w-full h-9 px-2 text-sm border border-border rounded-lg bg-background">
                  <option value="">Select an exam…</option>
                  {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  <option value="__other__">Other (not listed)</option>
                </select>
                {examId === '__other__' && (
                  <input
                    value={otherExamName}
                    onChange={(e) => setOtherExamName(e.target.value)}
                    placeholder="Enter exam name"
                    className="mt-2 w-full h-9 px-2 text-sm border border-border rounded-lg bg-background"
                  />
                )}
              </div>
              <div>
                <label className="text-xs text-primary font-medium block mb-1">Exam date <span className="text-red-500">*</span></label>
                <input type="date" min={todayIso()} value={examDate} onChange={(e) => setExamDate(e.target.value)} className="w-full h-9 px-2 text-sm border-2 border-primary/60 rounded-lg bg-background" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Target score <span className="text-muted-foreground/70">(optional)</span></label>
                <input value={targetScore} onChange={(e) => setTargetScore(e.target.value)} placeholder="e.g. 99 percentile" className="w-full h-9 px-2 text-sm border border-border rounded-lg bg-background" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Attempt <span className="text-muted-foreground/70">(optional)</span></label>
                <div className="flex gap-2">
                  {[['First', false], ['Retaking', true]].map(([label, val]) => (
                    <button key={String(label)} type="button" onClick={() => setIsResit((p) => (p === val ? null : (val as boolean)))}
                      className={cn('px-3 py-1.5 rounded-lg text-xs border', isResit === val ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-background')}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">The catalog may suggest a date — your value is the source of truth. Must be today or later.</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddExam}>Save exam</Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setExamId(''); setOtherExamName(''); setExamDate(''); setTargetScore(''); setIsResit(null); }}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1 cursor-pointer" size="lg">Back</Button>
        <Button onClick={() => onNext(goalType)} className="flex-1 cursor-pointer" size="lg" disabled={!goalType}>Continue</Button>
      </div>
    </div>
  );
}
